// src/App.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createChart} from 'lightweight-charts';
import { Box, useTheme, TextField, Button, IconButton, InputAdornment, Chip } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';

/* ------------------------------ Utilities ------------------------------ */
const PALETTE_19 = [
  '#4E79A7','#F28E2B','#59A14F','#E15759','#9C755F',
  '#76B7B2','#EDC948','#B07AA1','#FF9DA7','#BAB0AC',
  '#1F77B4','#FF7F0E','#2CA02C','#D62728','#9467BD',
  '#8C564B','#E377C2','#BCBD22','#17BECF',
];
// App.js (top of file)
const makeColorMap = (names) => {
  const map = new Map();
  names.forEach((n, i) => map.set(n, PALETTE_19[i % PALETTE_19.length]));
  return map;
};
const toBusinessDay = (yyyy_mm_dd) => ({
  year: +yyyy_mm_dd.slice(0, 4),
  month: +yyyy_mm_dd.slice(5, 7),
  day: +yyyy_mm_dd.slice(8,10),
});
const buildSeriesData = (name, chartData) => {
  const obj = chartData?.[name];
  if (!obj) return [];
  return Object.entries(obj)
    .filter(([d]) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    .sort(([a],[b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([d, val]) => ({ time: toBusinessDay(d), value: Number(val) })); 
};

/* ------------------------------ LineChart ------------------------------ */
function LineChart({ seriesList = [], suppressLabels = false }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesMap = useRef(new Map());
  const theme = useTheme();

  useEffect(() => {
    const el = containerRef.current;
    const chart = createChart(el, {
      width: el.clientWidth,
      height: 800,
        layout: {
        attributionLogo: false,
        background: { type: 'solid', color: theme.palette.background.default },
        textColor: theme.palette.text.primary,
        },
        rightPriceScale: { visible: true },
        leftPriceScale: { visible: false },
        timeScale: { borderVisible: false },
        grid: {
        horzLines: { color: theme.palette.divider },
        vertLines: { color: theme.palette.divider },
        },
    });
    chartRef.current = chart;

    const ro = new ResizeObserver(() => {
      chart.applyOptions({ width: el.clientWidth, height: 800 });
    });
    ro.observe(el);

    return () => { 
      // remove all series safely before removing the chart
      for (const [, s] of Array.from(seriesMap.current.entries())) {
        if (s) {
          try { chart.removeSeries(s); } catch {}
        }
      }
      seriesMap.current.clear();
      ro.disconnect(); 
      chart.remove(); 
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const map = seriesMap.current;

    const list = Array.isArray(seriesList) ? seriesList : [];

    // remove series no longer present
    for (const [name, s] of Array.from(map.entries())) {
      const stillThere = list.some(it => it && it.name === name);
      if (!stillThere) {
        if (s) {
          try { chart.removeSeries(s); } catch {}
        }
        map.delete(name);
      }
    }

    const wantLabels = !suppressLabels;

    // add/update current
    for (const item of list) {
      if (!item) continue;
      const { name, data = [], color, priceScaleId, lineWidth = 2 } = item;
      if (!name) continue;

      let s = map.get(name);
      if (!s) {
        s = chart.addLineSeries( {
          color,
          lineWidth,
          priceScaleId: priceScaleId || 'right',
          title: wantLabels ? name : '',
          lastValueVisible: wantLabels,
          priceLineVisible: wantLabels,
        });
        map.set(name, s);
      } else {
        s.applyOptions({
          color,
          lineWidth,
          priceScaleId: priceScaleId || 'right',
          title: wantLabels ? name : '',
          lastValueVisible: wantLabels,
          priceLineVisible: wantLabels,
        });
      }

      // never pass undefined; coerce to array
      s.setData(Array.isArray(data) ? data : []);
    }
  }, [seriesList, suppressLabels]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const list = Array.isArray(seriesList) ? seriesList : [];
    const hasLeft = list.some(s => s && s.priceScaleId === 'left');
    chart.applyOptions({ leftPriceScale: { visible: hasLeft } });
  }, [seriesList]);

    return (
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: 800,
            borderRadius: 2,
            boxShadow: theme.palette.shadow?.primary || '0 2px 6px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        />
      );
}

/* ------------------------------ ShortTable ------------------------------ */
function ShortTable({
    rows = [],
    selectedSet = new Set(),
    activeSymbol,
    onPick,
    onToggle,
    onNavigate,
    onClearAll,
    onShowAll,
    getColor = () => '#999',
    height = 800,
    query,
    onQueryChange,
    onSortToggle,
    sortAsc,
  }) {
    const theme = useTheme();

    const clickTimeoutRef = useRef(null);
    const clickedSymbolRef = useRef(null);

    const handleClick = (sym, e) => {
      if (e.ctrlKey || e.metaKey) {
        onToggle(sym);
        return;
      }
  
      if (clickTimeoutRef.current && clickedSymbolRef.current === sym) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
        clickedSymbolRef.current = null;
        onToggle(sym);
      } else {
        clearTimeout(clickTimeoutRef.current);
        clickedSymbolRef.current = sym;
        clickTimeoutRef.current = setTimeout(() => {
          onPick(sym, e);
          clickTimeoutRef.current = null;
          clickedSymbolRef.current = null;
        }, 250);
      }
    };

    useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          onNavigate(e.key === 'ArrowUp' ? 'up' : 'down');
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
        }
      };
    }, [onNavigate]);

    return (
      <Box
        sx={{
          width: 320,
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.palette.shadow?.primary || '0 2px 6px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            padding: '10px 12px',
            background: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
            <input
              placeholder="Search..."
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              style={{ flex: 1, padding: '6px 8px', border: `1px solid ${theme.palette.divider}`, borderRadius: 6 }}
            />
            {selectedSet.size > 0 && (
              <div style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                background: '#F28E2B',
                color: 'white',
                borderRadius: 8,
                padding: '2px 6px',
                fontSize: 12,
                fontWeight: 'bold',
              }}>
                {selectedSet.size}
                <button onClick={onClearAll} style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 14,
                  lineHeight: 1,
                }}>
                  ×
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onShowAll} style={btnStyle}>
              Show All
            </button>
            <button onClick={onSortToggle} style={btnStyle} title={sortAsc ? 'Sort Descending' : 'Sort Ascending'}>
              {sortAsc ? '↓' : '↑'}
            </button>
          </div>
        </div>
  
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 90px',
            fontSize: 13,
            padding: '8px 10px',
            color: '#666',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <div>Symbol</div>
          <div style={{ textAlign: 'right' }}>Outshort</div>
        </div>
  
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {rows.map(({ sym, value }, i) => {        {/* <-- add index param here */}
            const isOn = selectedSet.has(sym);
            const isActive = activeSymbol === sym;
            const color = getColor(sym);
            return (
              <button
                key={sym}
                //onClick={(e) => (e.ctrlKey || e.metaKey) ? onToggle(sym) : onPick(sym)}
                onClick={(e) => handleClick(sym, e)}
                title={isOn ? 'Selected' : 'Select'}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 90px',
                  alignItems: 'center',
                  width: '100%',
                  padding: '8px 10px',
                  border: 0,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  background: isActive
                    ? theme.palette.action.selected
                    : (i % 2 === 0
                        ? theme.palette.table?.odd || theme.palette.background.paper
                        : theme.palette.table?.even || theme.palette.background.default),
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      border: `2px solid ${isOn ? color : '#bbb'}`,
                      background: isOn ? color : 'transparent',
                    }}
                  />
                  {sym}
                </div>
                <div style={{ textAlign: 'right', color: '#333' }}>
                  {Number.isFinite(value) ? value.toFixed(2) : '—'}
                </div>
              </button>
            );
          })}
        </div>
      </Box>
    );
  }
  

/* ------------------------------ Main App ------------------------------ */
export default function App() {
  // data
  const [shortChartData, setShortChartData] = useState({});
  const [query, setQuery] = useState('');
  const [sortAsc, setSortAsc] = useState(false);
  const [shortSelected, setShortSelected] = useState([]);
  const [shortActive, setShortActive] = useState(null);
  const [shortLoading, setShortLoading] = useState(true);
  const [shortPriceData, setShortPriceData] = useState({}); // {SYM: [{time,value}]}
  const fetchingPriceRef = useRef(new Set());

  // single-select toggles
  const PRICE_COLOR = '#0D6EFD';
  const OUTSHORT_SINGLE_COLOR = '#E86A10';
  const [showShortLine, setShowShortLine] = useState(true);
  const [showPriceLine, setShowPriceLine] = useState(true);
  const csrfToken = useSelector((state) => state.csrfToken);
  const activeSingleSym = shortSelected.length === 1 ? shortSelected[0] : null;
  useEffect(() => { setShowShortLine(true); setShowPriceLine(true); }, [activeSingleSym]);

  // fetch outstanding short (inline, with regular fetch)
  useEffect(() => {
    (async () => {
      setShortLoading(true);
      try {
        const res = await fetch(`${window.config.base_api_url}/api/outstanding_short/set100`, {
                headers: {
                    'Content-Type': 'application/json' ,
                    'xsrf-token': csrfToken,
                }
            });
        const json = await res.json();
        // Normalization (same as earlier)
        const payload = json?.data || {};
        const rawData = payload.data || {};
        const symbols = Array.isArray(payload.symbols) ? payload.symbols : Object.keys(rawData);
        const chart_data = {};
        for (const sym of symbols) {
          const arr = Array.isArray(rawData[sym]) ? rawData[sym] : [];
          const obj = {};
          for (const item of arr) {
            const d = item?.date;
            const v = item?.outshort;
            if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) && Number.isFinite(Number(v))) {
              obj[d] = Number(v);
            }
          }
          if (Object.keys(obj).length) chart_data[sym] = obj;
        }
        setShortChartData(chart_data);
        const all = Object.keys(chart_data).sort();
        setShortSelected(all);       // start with all
        setShortActive(null);
      } catch (e) {
        console.error(e);
        setShortChartData({});
        setShortSelected([]);
      } finally {
        setShortLoading(false);
      }
    })();
  }, []);

  // derived helpers
  const shortAllSymbols = useMemo(() => Object.keys(shortChartData || {}).sort(), [shortChartData]);
  const shortColorMap = useMemo(() => makeColorMap(shortAllSymbols), [shortAllSymbols]);

  // right table rows (latest values)
  const shortRows = useMemo(() => {
    const rows = shortAllSymbols.map(sym => {
      const obj = shortChartData[sym] || {};
      const dates = Object.keys(obj).sort();
      const last = dates.length ? Number(obj[dates[dates.length - 1]]) : null;
      return { sym, value: Number.isFinite(last) ? last : null };
    });
    rows.sort((a, b) => {
      const valA = a.value ?? -Infinity;
      const valB = b.value ?? -Infinity;
      return sortAsc ? valA - valB : valB - valA;
    });
    return rows;
  }, [shortAllSymbols, shortChartData, sortAsc]);

  // build series (single vs multi)
  const shortSeriesList = useMemo(() => {
    const lines = [];
    if (shortSelected.length === 1) {
      const sym = shortSelected[0];
      const outshortData = buildSeriesData(sym, shortChartData);
      const priceData = shortPriceData[sym];

      if (showPriceLine && priceData && priceData.length) {
        lines.push({
          name: `${sym} (Price)`,
          color: PRICE_COLOR,
          lineWidth: 2,
          priceScaleId: 'left',
          data: priceData,
        });
      }
      if (showShortLine) {
        lines.push({
          name: `${sym} (Outshort)`,
          color: OUTSHORT_SINGLE_COLOR,
          lineWidth: 2,
          priceScaleId: 'right',
          data: outshortData,
        });
      }
    } else {
      for (const name of shortSelected) {
        lines.push({
          name,
          color: shortColorMap.get(name),
          lineWidth: 2,
          priceScaleId: 'right',
          data: buildSeriesData(name, shortChartData),
        });
      }
    }
    return lines;
  }, [shortSelected, shortChartData, shortColorMap, shortPriceData, showPriceLine, showShortLine]);

  // find global date range so we fetch price with same range
  const shortDateRange = useMemo(() => {
    const allDates = [];
    for (const sym of Object.keys(shortChartData || {})) {
      allDates.push(...Object.keys(shortChartData[sym] || {}));
    }
    if (!allDates.length) return null;
    allDates.sort();
    return { start: allDates[0], end: allDates[allDates.length - 1] };
  }, [shortChartData]);

  // fetch price for single symbol
  useEffect(() => {
    if (!shortDateRange) return;
    if (shortSelected.length !== 1) return;
    const sym = shortSelected[0];
    if (shortPriceData[sym] || fetchingPriceRef.current.has(sym)) return;

    (async () => {
      try {
        fetchingPriceRef.current.add(sym);
        const params = new URLSearchParams({
          symbol: sym,
          exchange: 'SET',
          interval: '1D',
          start_date: shortDateRange.start,
          end_date: shortDateRange.end,
        });
        const res = await fetch(`${window.config.base_api_url}/api/prices?${params.toString()}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'xsrf-token': csrfToken,
                }
            });
        const json = await res.json();
        const rows = json?.data || [];
        const data = rows.map(r => ({
          time: toBusinessDay(r.date),
          value: Number(r.price),
        })).filter(p => Number.isFinite(p.value));
        setShortPriceData(prev => ({ ...prev, [sym]: data }));
      } catch (e) {
        console.error(e);
      } finally {
        fetchingPriceRef.current.delete(sym);
      }
    })();
  }, [shortSelected, shortDateRange, shortPriceData]);

  // table actions
  const addShortSymbol = (name) => setShortSelected(prev => (prev.includes(name) ? prev : [...prev, name]));
  const removeShortSymbol = (name) => setShortSelected(prev => prev.filter(n => n !== name));
  const clearQuery     = () => setQuery('');
  const clearAllShort  = () => { 
    setShortSelected([]); 
    setShortActive(null); 
    clearQuery();
  };

  const showAllShort = () => { 
    const all = Object.keys(shortChartData || {}).sort();
    setShortSelected(all);
    setShortActive(null);
  };

  const pickShortSymbol = (sym, e) => {
    if (e.ctrlKey || e.metaKey) {
      setShortSelected(prev => (prev.includes(sym) ? prev.filter(n => n !== sym) : [...prev, sym]));
    } else {
      setShortSelected([sym]);
    }
    setShortActive(sym);
  };

  const toggleShortSymbol = (sym) => {
    setShortSelected(prev => (prev.includes(sym) ? prev.filter(n => n !== sym) : [...prev, sym]));
    setShortActive(sym);
  };

  const navigateShort = (direction) => {
    if (!visible.length) return;
    const currentIdx = visible.findIndex(r => r.sym === shortActive);
    
    let nextIdx = currentIdx;
    if (currentIdx === -1) {
        nextIdx = 0;
    } else if (direction === 'up') {
        nextIdx = Math.max(0, currentIdx - 1);
    } else if (direction === 'down') {
        nextIdx = Math.min(visible.length - 1, currentIdx + 1);
    }

    const newSym = visible[nextIdx]?.sym;
    if (newSym) {
      setShortActive(newSym);
      setShortSelected([newSym]);
    }
  };

  const toggleSort = () => setSortAsc(prev => !prev);

  useEffect(() => {
    console.log("length =", shortSelected.length);
  }, [shortSelected]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return shortRows;
    return shortRows
      .filter(row => row && row.sym && row.sym.toLowerCase().startsWith(q))
      .slice();
  }, [shortRows, query]);

  /* ------------------------------ UI ------------------------------ */
  return (
    <div style={{ padding: 20, fontFamily: 'Inter, system-ui, Arial, sans-serif' }}>
      {/* Title + single-select legend toggles */}
      <div style={{ display:'flex', alignItems:'center', gap:16, margin:'6px 0 12px' }}>
        <h2 style={{ margin:0 }}>S50 Outstanding Short</h2>

        {shortSelected.length === 1 && (
          <div style={{ display:'flex', gap:16, fontSize:14 }}>
            <label style={legendItemStyle}>
              <input
                type="checkbox"
                checked={showShortLine}
                onChange={e => setShowShortLine(e.target.checked)}
                style={{ display:'none' }}
              />
              <span style={legendDot(OUTSHORT_SINGLE_COLOR, showShortLine)} />
              <span>Outshort</span>
            </label>
            <label style={legendItemStyle}>
              <input
                type="checkbox"
                checked={showPriceLine}
                onChange={e => setShowPriceLine(e.target.checked)}
                style={{ display:'none' }}
              />
              <span style={legendDot(PRICE_COLOR, showPriceLine)} />
              <span>Price</span>
            </label>
          </div>
        )}
      </div>

      {shortLoading && <div>Loading…</div>}
      {!shortLoading && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:16 }}>
          <div>
            <LineChart
              seriesList={shortSeriesList}
              suppressLabels={shortSelected.length === 1}
            />
          </div>
          <ShortTable
            rows={visible}
            selectedSet={new Set(shortSelected)}
            activeSymbol={shortActive}
            onPick={pickShortSymbol}
            onToggle={toggleShortSymbol}
            onNavigate={navigateShort}
            onClearAll={clearAllShort}
            onShowAll={showAllShort}
            getColor={(name) => shortColorMap.get(name)}
            query={query}
            onQueryChange={setQuery}
            onSortToggle={toggleSort}
            sortAsc={sortAsc}
          />
        </div>
      )}
    </div>
  );
}

/* ------------------------------ Inline styles ------------------------------ */
const btnStyle = {
  fontSize: 12, padding: '4px 8px', border: '1px solid #ddd',
  background: 'white', borderRadius: 6, cursor: 'pointer'
};
const legendItemStyle = {
  display: 'inline-flex', alignItems:'center', gap:8, cursor:'pointer'
};
const legendDot = (color, on) => ({
  width: 10, height: 10, borderRadius: '50%',
  border: `2px solid ${color}`, background: on ? color : 'transparent'
});
